/**
 * Types of Triggers based on triggering event types
 */

import {
  EventTriggeringConditionType,
  TriggerScopeType,
} from '../../../generated/graphql';
import {type OptionType} from './TriggersTable/utilTypes';

export {EventTriggeringConditionType} from '../../../generated/graphql';

/**
 * The subset of EventTriggeringConditionType values that the frontend
 * currently handles. New values can be added to the backend GraphQL schema
 * without breaking the frontend; add them here when frontend support is ready.
 */
const KNOWN_EVENT_TYPES = [
  EventTriggeringConditionType.AddArtifactAlias,
  EventTriggeringConditionType.CreateArtifact,
  EventTriggeringConditionType.LinkModel,
  EventTriggeringConditionType.UnlinkArtifact,
  EventTriggeringConditionType.UpdateArtifactAlias,
  EventTriggeringConditionType.AddArtifactTag,
  EventTriggeringConditionType.RemoveArtifactTag,
  EventTriggeringConditionType.AddCollectionTag,
  EventTriggeringConditionType.RemoveCollectionTag,
  EventTriggeringConditionType.RunMetric,
  EventTriggeringConditionType.RunMetricChange,
  EventTriggeringConditionType.RunState,
  EventTriggeringConditionType.RunMetricZscore,
  EventTriggeringConditionType.WeaveMetricThreshold,
  EventTriggeringConditionType.SandboxState,
] as const;

export type KnownEventType = (typeof KNOWN_EVENT_TYPES)[number];

export function isKnownEventType(
  eventType: EventTriggeringConditionType
): eventType is KnownEventType {
  return (
    KNOWN_EVENT_TYPES as readonly EventTriggeringConditionType[]
  ).includes(eventType);
}

export type EventTypeMetadata = {
  text: string;
  defaultScopeType: TriggerScopeType;
};

/**
 * Template variable definition for trigger actions.
 * Used by useTriggerTemplateVariables hook to return backend-fetched variables.
 */
export type TemplateValue = {
  value: string;
  help: string;
};

export const AddAliasEventMetadata: EventTypeMetadata = {
  text: 'An artifact alias is added',
  defaultScopeType: TriggerScopeType.Project,
};

export const CreateArtifactEventMetadata: EventTypeMetadata = {
  text: 'A new version is added to an artifact',
  defaultScopeType: TriggerScopeType.ArtifactCollection,
};

export const UpdateAliasEventMetadata: EventTypeMetadata = {
  text: 'An artifact alias is updated',
  defaultScopeType: TriggerScopeType.Project,
};

export const LinkModelEventMetadata: EventTypeMetadata = {
  text: 'A new version is linked to a collection',
  defaultScopeType: TriggerScopeType.ArtifactCollection,
};

export const UnlinkArtifactEventMetadata: EventTypeMetadata = {
  text: 'A version is unlinked from a collection',
  defaultScopeType: TriggerScopeType.ArtifactCollection,
};

export const AddArtifactTagEventMetadata: EventTypeMetadata = {
  text: 'An artifact tag is added',
  defaultScopeType: TriggerScopeType.Project,
};

export const RemoveArtifactTagEventMetadata: EventTypeMetadata = {
  text: 'An artifact tag is removed',
  defaultScopeType: TriggerScopeType.Project,
};

export const AddCollectionTagEventMetadata: EventTypeMetadata = {
  text: 'A collection tag is added',
  defaultScopeType: TriggerScopeType.Project,
};

export const RemoveCollectionTagEventMetadata: EventTypeMetadata = {
  text: 'A collection tag is removed',
  defaultScopeType: TriggerScopeType.Project,
};

export const RunMetricEventMetadata: EventTypeMetadata = {
  text: 'Run metrics threshold met',
  defaultScopeType: TriggerScopeType.Project,
};

export const RunMetricChangeEventMetadata: EventTypeMetadata = {
  text: 'Run metrics change threshold met',
  defaultScopeType: TriggerScopeType.Project,
};

export const RunStateEventMetadata: EventTypeMetadata = {
  text: 'Run status change',
  defaultScopeType: TriggerScopeType.Project,
};

export const RunMetricZscoreEventMetadata: EventTypeMetadata = {
  text: 'Run metrics z-score threshold met',
  defaultScopeType: TriggerScopeType.Project,
};

export const WeaveMetricThresholdEventMetadata: EventTypeMetadata = {
  text: 'Weave metric threshold',
  defaultScopeType: TriggerScopeType.Project,
};

export const SandboxStateEventMetadata: EventTypeMetadata = {
  text: 'Sandbox status change',
  defaultScopeType: TriggerScopeType.Project,
};

export const EventTypeToMetadata: Record<KnownEventType, EventTypeMetadata> = {
  [EventTriggeringConditionType.AddArtifactAlias]: AddAliasEventMetadata,
  [EventTriggeringConditionType.CreateArtifact]: CreateArtifactEventMetadata,
  [EventTriggeringConditionType.LinkModel]: LinkModelEventMetadata,
  [EventTriggeringConditionType.UnlinkArtifact]: UnlinkArtifactEventMetadata,
  [EventTriggeringConditionType.UpdateArtifactAlias]: UpdateAliasEventMetadata,
  [EventTriggeringConditionType.AddArtifactTag]: AddArtifactTagEventMetadata,
  [EventTriggeringConditionType.RemoveArtifactTag]:
    RemoveArtifactTagEventMetadata,
  [EventTriggeringConditionType.AddCollectionTag]:
    AddCollectionTagEventMetadata,
  [EventTriggeringConditionType.RemoveCollectionTag]:
    RemoveCollectionTagEventMetadata,
  [EventTriggeringConditionType.RunMetric]: RunMetricEventMetadata,
  [EventTriggeringConditionType.RunMetricChange]: RunMetricChangeEventMetadata,
  [EventTriggeringConditionType.RunState]: RunStateEventMetadata,
  [EventTriggeringConditionType.RunMetricZscore]: RunMetricZscoreEventMetadata,
  [EventTriggeringConditionType.WeaveMetricThreshold]:
    WeaveMetricThresholdEventMetadata,
  [EventTriggeringConditionType.SandboxState]: SandboxStateEventMetadata,
};

/**
 * Short, chip-style labels for each known event type.
 *
 * Distinct from `EventTypeMetadata.text`, which holds full descriptive sentences
 * used by the trigger configuration wizard (e.g. "A new version is added to an
 * artifact"). These labels are the user-facing column copy rendered by
 * `EventTypeChip` and the Automations Hub filter dropdowns.
 */
export const EventTypeChipLabels: Record<KnownEventType, string> = {
  [EventTriggeringConditionType.AddArtifactAlias]: 'New alias',
  [EventTriggeringConditionType.CreateArtifact]: 'New version',
  [EventTriggeringConditionType.LinkModel]: 'New version',
  [EventTriggeringConditionType.UnlinkArtifact]: 'Version unlinked',
  // Update-alias triggers are deprecated; this label is only rendered for
  // legacy rows that still reference the type.
  [EventTriggeringConditionType.UpdateArtifactAlias]: 'Update alias',
  [EventTriggeringConditionType.AddArtifactTag]: 'New version tag',
  [EventTriggeringConditionType.RemoveArtifactTag]: 'Removed version tag',
  [EventTriggeringConditionType.AddCollectionTag]: 'New collection tag',
  [EventTriggeringConditionType.RemoveCollectionTag]: 'Removed collection tag',
  [EventTriggeringConditionType.RunMetric]: 'Run metrics threshold met',
  [EventTriggeringConditionType.RunMetricChange]:
    'Run metrics change threshold met',
  [EventTriggeringConditionType.RunState]: 'Run status change',
  [EventTriggeringConditionType.RunMetricZscore]:
    'Run metrics z-score threshold met',
  [EventTriggeringConditionType.WeaveMetricThreshold]: 'Weave metric threshold',
  [EventTriggeringConditionType.SandboxState]: 'Sandbox status change',
};

/**
 * Weave event types that are available to all users without requiring automation access.
 */
export const WEAVE_EVENT_TYPES: EventTriggeringConditionType[] = [
  EventTriggeringConditionType.WeaveMetricThreshold,
];

/**
 * Check if an event type is a Weave event (available to all users).
 */
export const isWeaveEventType = (
  eventType: EventTriggeringConditionType
): boolean => {
  return WEAVE_EVENT_TYPES.includes(eventType);
};

/**
 * Run event types fire on run activity, not on artifacts or collections.
 */
export const RUN_EVENT_TYPES: EventTriggeringConditionType[] = [
  EventTriggeringConditionType.RunMetric,
  EventTriggeringConditionType.RunMetricChange,
  EventTriggeringConditionType.RunMetricZscore,
  EventTriggeringConditionType.RunState,
];

/**
 * Check if an event type is a run event.
 */
export const isRunEventType = (
  eventType: EventTriggeringConditionType
): boolean => {
  return RUN_EVENT_TYPES.includes(eventType);
};

export type TriggerOptionType = OptionType & {
  isDisabled?: boolean;
  disabledReason?: string;
};

export const makeTriggerOptionForSelect = (
  eventType: KnownEventType
): OptionType => {
  return {
    label: EventTypeToMetadata[eventType].text,
    value: eventType,
  };
};

/**
 * Create a trigger option with optional disabled state.
 * Weave event types are disabled if the project has no weave data (models-only).
 */
export const makeTriggerOptionWithDisabledState = (
  eventType: KnownEventType,
  isModelsOnly = false
): TriggerOptionType => {
  const label = EventTypeToMetadata[eventType].text;
  if (isWeaveEventType(eventType) && isModelsOnly) {
    return {
      label,
      value: eventType,
      isDisabled: true,
      disabledReason: 'Log traces with Weave to enable this event type',
    };
  }

  return {
    label,
    value: eventType,
  };
};

export const allKnownEventTypes = Object.keys(
  EventTypeToMetadata
) as KnownEventType[];
